import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchVendors } from "../state/slices/vendorSlice";
import { fetchCoupons } from "../state/slices/couponSlice";

const CATEGORY_OPTIONS = [
  "Attractions",
  "Beer / Drinks",
  "Accommodations",
  "Coffee Shops",
  "Dining",
  "Retail",
  "Transportation",
  "Golf / Mini",
  "Tours / Excursions",
  "Spa / Health",
  "Museums",
  "Misc",
];

const VendorTwo = () => {
  let [grid, setGrid] = useState(false);
  let [active, setActive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryVendors, setCategoryVendors] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [activeCity, setActiveCity] = useState(null);
  const [activeRating, setActiveRating] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const ITEMS_PER_PAGE = 12;

  const dispatch = useDispatch();
  const rawVendors = useSelector((state) => state.vendors.vendors);
  const rawCoupons = useSelector((state) => state.coupons.coupons);
  const allVendors = useMemo(() => Object.values(rawVendors), [rawVendors]);
  const allCoupons = useMemo(() => Object.values(rawCoupons), [rawCoupons]);
  const navigate = useNavigate();

  const vendorRatings = useMemo(() => {
    const map = {};
    allCoupons.forEach(c => {
      if (c.average_rating != null) {
        if (!map[c.vendor_id]) map[c.vendor_id] = { total: 0, count: 0 };
        map[c.vendor_id].total += parseFloat(c.average_rating);
        map[c.vendor_id].count += 1;
      }
    });
    const result = {};
    Object.entries(map).forEach(([vid, { total, count }]) => {
      result[vid] = { average: Math.round((total / count) * 10) / 10, count };
    });
    return result;
  }, [allCoupons]);

  const uniqueCities = [...new Set(allVendors.map(v => v.city).filter(Boolean))].sort();

  const baseVendors = (selectedCategory ? categoryVendors : allVendors)
    .filter(v => !activeCity || v.city === activeCity);

  const vendorRatingCounts = {};
  [1,2,3,4,5].forEach(stars => {
    vendorRatingCounts[stars] = baseVendors.filter(v =>
      Math.round(vendorRatings[v.id]?.average || 0) >= stars
    ).length;
  });

  const maxVendorRatingCount = Math.max(...[1,2,3,4,5].map(s => vendorRatingCounts[s] || 0), 1);

  const displayedVendors = baseVendors
    .filter(v => !activeRating || Math.round(vendorRatings[v.id]?.average || 0) >= activeRating)
    .filter(v => !searchQuery || v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()));

  const totalPages = Math.max(1, Math.ceil(displayedVendors.length / ITEMS_PER_PAGE));
  const pagedVendors = displayedVendors.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const WINDOW_SIZE = 8;
  const windowStart = Math.max(1, Math.min(currentPage - 3, totalPages - WINDOW_SIZE + 1));
  const windowEnd = Math.min(totalPages, windowStart + WINDOW_SIZE - 1);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  let sidebarController = () => {
    setActive(!active);
  };

  const handleVendorNavigateClick = (id) => {
      navigate(`/vendor/${id}`)
  };

  const handleCategoryClick = async (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
      setCategoryVendors([]);
      return;
    }
    setSelectedCategory(category);
    setCategoryLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/by-category/${encodeURIComponent(category)}`);
      const data = await res.json();
      setCategoryVendors(Array.isArray(data) ? data : []);
    } catch {
      setCategoryVendors([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
      dispatch(fetchVendors());
      dispatch(fetchCoupons());
      fetch(`${process.env.REACT_APP_API_URL}/api/vendors/category-counts`)
        .then(r => r.json())
        .then(data => setCategoryCounts(data))
        .catch(() => {});
    }, [dispatch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, activeCity, activeRating, searchQuery]);


  return (
    <section className='vendor-two py-80'>
      <div className={`side-overlay ${active && "show"}`}></div>
      <div className='container container-lg'>
        {/* Top Search */}
        <div className='d-flex align-items-center justify-content-between flex-wrap mb-48 gap-16'>
          <form onSubmit={handleSearch} className='input-group w-100 max-w-418'>
            <input
              type='text'
              className='form-control common-input rounded-start-3'
              placeholder='Search vendors by name...'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              type='submit'
              className='input-group-text border-0 bg-main-two-600 rounded-end-3 text-white text-2xl hover-bg-main-two-700 px-24'
            >
              <i className='ph ph-magnifying-glass' />
            </button>
          </form>
          <div className='d-flex align-items-center justify-content-between justify-content-sm-end gap-16 flex-grow-1'>
            <div className='text-gray-600 text-md flex-shrink-0'>
              {" "}
              <span className='text-neutral-900 fw-semibold'>{displayedVendors.length}</span> Results
              Found
            </div>
            <div className='d-flex align-items-center gap-8 d-sm-flex d-none'>
              <button
                onClick={() => setGrid(false)}
                type='button'
                className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${
                  grid === false && "border-main-600 text-white bg-main-600"
                }`}
              >
                <i className='ph ph-squares-four' />
              </button>
              <button
                onClick={() => setGrid(true)}
                type='button'
                className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                  grid === true && "border-main-600 text-white bg-main-600"
                }`}
              >
                <i className='ph-bold ph-list-dashes' />
              </button>
            </div>
            <button
              onClick={sidebarController}
              type='button'
              className='w-48 h-48 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn'
            >
              <i className='ph-bold ph-funnel' />
            </button>
          </div>
        </div>
        {/* Top Search End */}
        <div className='row'>
          <div className='col-xl-3 col-lg-4'>
            <div className={`shop-sidebar ${active && "active"}`}>
              <button
                onClick={sidebarController}
                type='button'
                className='shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-main-600 bg-main-600 text-white rounded-circle hover-bg-main-700 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-700'
              >
                <i className='ph ph-x' />
              </button>
              <div className='d-flex flex-column gap-12 px-lg-0 px-3 py-lg-0 py-4'>
                <div className='border border-gray-50 rounded-8 p-24'>
                  <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24'>
                    Vendor Coupon Categories
                  </h6>
                  <ul className='max-h-540 overflow-y-auto scroll-sm'>
                    <li className='mb-16'>
                      <button
                        type='button'
                        onClick={() => { setSelectedCategory(null); setCategoryVendors([]); }}
                        className={`text-start w-100 border-0 bg-transparent fw-medium ${!selectedCategory ? "text-main-800" : "text-gray-900 hover-text-main-800"}`}
                      >
                        All Vendors
                      </button>
                    </li>
                    {CATEGORY_OPTIONS.map((cat, idx) => (
                      <li key={cat} className={`d-flex align-items-center justify-content-between ${idx === CATEGORY_OPTIONS.length - 1 ? 'mb-0' : 'mb-16'}`}>
                        <button
                          type='button'
                          onClick={() => handleCategoryClick(cat)}
                          className={`text-start border-0 bg-transparent ${selectedCategory === cat ? "text-main-600 fw-semibold" : "text-gray-900 hover-text-main-800"}`}
                        >
                          {cat}
                        </button>
                        <span className='text-xs text-gray-500 flex-shrink-0'>
                          {categoryCounts[cat] ?? 0}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32'>
                  <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24'>
                    Filter by Rating
                  </h6>
                  {[5,4,3,2,1].map((stars, idx) => (
                    <div
                      key={stars}
                      className={`flex-align gap-8 position-relative ${idx < 4 ? 'mb-20' : 'mb-0'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveRating(activeRating === stars ? null : stars)}
                    >
                      <div className='common-check common-radio mb-0'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='vendorRating'
                          id={`vendor-rating${stars}`}
                          checked={activeRating === stars}
                          onChange={() => setActiveRating(activeRating === stars ? null : stars)}
                        />
                      </div>
                      <div
                        className='progress w-100 bg-gray-100 rounded-pill h-8'
                        role='progressbar'
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(((vendorRatingCounts[stars] || 0) / maxVendorRatingCount) * 100)}
                      >
                        <div
                          className='progress-bar bg-main-600 rounded-pill'
                          style={{ width: `${Math.round(((vendorRatingCounts[stars] || 0) / maxVendorRatingCount) * 100)}%` }}
                        />
                      </div>
                      <div className='flex-align gap-4'>
                        {[1,2,3,4,5].map(s => (
                          <span key={s} className={`text-xs fw-medium d-flex ${s <= stars ? 'text-warning-600' : 'text-gray-400'}`}>
                            <i className='ph-fill ph-star' />
                          </span>
                        ))}
                      </div>
                      <span className='text-gray-900 flex-shrink-0'>({vendorRatingCounts[stars] || 0})</span>
                    </div>
                  ))}
                  {activeRating && (
                    <button
                      type='button'
                      className='btn btn-outline-main h-32 flex-align mt-20 text-sm'
                      onClick={e => { e.stopPropagation(); setActiveRating(null); }}
                    >
                      Clear Rating
                    </button>
                  )}
                </div>
                <div className='border border-gray-50 rounded-8 p-24'>
                  <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24'>
                    Filter by Location
                  </h6>
                  <ul className='max-h-540 overflow-y-auto scroll-sm'>
                    <li className='mb-16'>
                      <div className='form-check common-check common-radio'>
                        <input
                          className='form-check-input'
                          type='radio'
                          name='vendorCity'
                          id='vendor-city-all'
                          checked={activeCity === null}
                          onChange={() => setActiveCity(null)}
                        />
                        <label className='form-check-label' htmlFor='vendor-city-all'>
                          All Locations
                        </label>
                      </div>
                    </li>
                    {uniqueCities.map((city) => (
                      <li key={city} className='mb-16'>
                        <div className='form-check common-check common-radio'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='vendorCity'
                            id={`vendor-city-${city}`}
                            checked={activeCity === city}
                            onChange={() => setActiveCity(city)}
                          />
                          <label className='form-check-label' htmlFor={`vendor-city-${city}`}>
                            {city}
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className='col-xl-9 col-lg-8'>
            {/* Vendors Start */}
            {selectedCategory && (
              <p className='text-gray-500 mb-16 text-sm'>
                {categoryLoading ? "Loading..." : `Showing vendors with coupons in: `}
                {!categoryLoading && <span className='fw-semibold text-main-600'>{selectedCategory}</span>}
              </p>
            )}
            <div className={`list-grid-wrapper vendors-two-item-wrapper grid-cols-3 ${grid && "list-view"}`}>
              {
                categoryLoading ? (
                  <p className='text-gray-500'>Loading vendors...</p>
                ) : Array.isArray(pagedVendors) && pagedVendors.length > 0 ? (
                    pagedVendors.map((vendor, key) => {
                        return (
                                      <div key={key} className='vendors-two-item rounded-12 overflow-hidden bg-color-three border border-neutral-50 hover-border-main-two-600 transition-2'>
                                        <div className='vendors-two-item__top bg-overlay style-two position-relative'>
                                          <div className='vendors-two-item__thumbs h-210' style={{ overflow: 'hidden' }}>
                                            {vendor.banner_image ? (
                                                <img
                                                  src={vendor.banner_image}
                                                  alt=''
                                                  className='cover-img'
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
                                                    <i className="bi bi-shop" style={{ fontSize: '4rem', color: '#9ca3af' }} />
                                                </div>
                                            )}
                                          </div>
                                          <div className='position-absolute top-0 inset-inline-start-0 w-100 h-100 p-24 z-1 d-flex flex-column justify-content-between'>
                                            <div className='d-flex align-items-center justify-content-between'>
                                              <span className='w-80 h-80 flex-center bg-white rounded-circle flex-shrink-0 overflow-hidden'>
                                                {vendor.image ? (
                                                  <img src={vendor.image} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                  <i className="bi bi-shop" style={{ fontSize: '1.75rem', color: '#9ca3af' }} />
                                                )}
                                              </span>
                                            </div>
                                            <div className='mt-16'>
                                              <h6 className='text-white fw-semibold mb-12'>
                                                <Link to='/vendor-two-details' className=''>
                                                  {vendor.vendor_name}
                                                </Link>
                                              </h6>
                                              <div className='flex-align gap-6'>
                                                <div className='flex-align gap-8'>
                                                  {[1,2,3,4,5].map(s => (
                                                    <span key={s} className={`text-15 fw-medium d-flex ${s <= Math.round(vendorRatings[vendor.id]?.average || 0) ? 'text-warning-600' : 'text-gray-400'}`}>
                                                      <i className='ph-fill ph-star' />
                                                    </span>
                                                  ))}
                                                </div>
                                                <span className='text-xs fw-medium text-white'>
                                                  {vendorRatings[vendor.id]?.average ?? "—"}
                                                </span>
                                                <span className='text-xs fw-medium text-white'>
                                                  ({vendorRatings[vendor.id]?.count ?? 0})
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className='vendors-two-item__content p-24 flex-grow-1'>
                                          <div className='d-flex flex-column gap-14'>
                                            <div className='flex-align gap-8'>
                                              <span className='flex-center text-main-two-600 text-2xl flex-shrink-0'>
                                                <i className='ph ph-map-pin-line' />
                                              </span>
                                              <p className='text-md text-gray-900'>
                                                {vendor.address}, {vendor.city}, {vendor.zipcode}
                                              </p>
                                            </div>
                                            <div className='flex-align gap-8'>
                                              <span className='flex-center text-main-two-600 text-2xl flex-shrink-0'>
                                                <i className='ph ph-envelope-simple' />
                                              </span>
                                              <a
                                                href='mailto:info@watch.com'
                                                className='text-md text-gray-900 hover-text-main-60'
                                              >
                                                {vendor.email}
                                              </a>
                                            </div>
                                            <div className='flex-align gap-8'>
                                              <span className='flex-center text-main-two-600 text-2xl flex-shrink-0'>
                                                <i className='ph ph-phone' />
                                              </span>
                                              <a
                                                href='tel:{vendor.phone_number}'
                                                className='text-md text-gray-900 hover-text-main-60'
                                              >
                                                {vendor.vendor_name}
                                              </a>
                                            </div>
                                          </div>
                                          <div
                                            onClick={()=> handleVendorNavigateClick(vendor.id)}
                                            className='btn bg-neutral-600 hover-bg-neutral-700 text-white py-12 px-24 rounded-8 flex-center gap-8 fw-medium mt-24'
                                          >
                                            Visit Vendor Page
                                            <span className='text-xl d-flex text-main-two-600'>
                                              {" "}
                                              <i className='ph ph-storefront' />
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      )
                                          })
                                      ) : (
                                          <p className='text-gray-500'>
                                            {selectedCategory ? `No vendors found for "${selectedCategory}".` : "No vendors available."}
                                          </p>
                                      )
                                  }
            </div>
            {/* Vendors End */}
            {/* Pagination Start */}
            {totalPages > 1 && (
              <ul className='pagination flex-center flex-wrap gap-16 mt-40'>
                <li className='page-item'>
                  <button
                    type='button'
                    disabled={currentPage === 1}
                    onClick={() => handlePageClick(currentPage - 1)}
                    className='page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100 bg-transparent'
                    style={{ opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? 'default' : 'pointer' }}
                  >
                    <i className='ph-bold ph-arrow-left' />
                  </button>
                </li>
                {visiblePages.map(page => (
                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                    <button
                      type='button'
                      onClick={() => handlePageClick(page)}
                      className={`page-link h-64 w-64 flex-center text-md rounded-8 fw-medium border ${currentPage === page ? 'bg-main-600 text-white border-main-600' : 'text-neutral-600 border-gray-100 bg-transparent'}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {String(page).padStart(2, '0')}
                    </button>
                  </li>
                ))}
                <li className='page-item'>
                  <button
                    type='button'
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageClick(currentPage + 1)}
                    className='page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100 bg-transparent'
                    style={{ opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? 'default' : 'pointer' }}
                  >
                    <i className='ph-bold ph-arrow-right' />
                  </button>
                </li>
              </ul>
            )}
            {/* Pagination End */}
          </div>
        </div>
      </div>
    </section>
  );
}

export default VendorTwo
